// src/utils/bookingCase.js
//
// Pass 50A — the canonical SAS booking-CASE model. A booking case is the parent commercial/
// customer enquiry (stored in `bookingRequests`, extended ADDITIVELY so legacy single-session
// requests still normalise). It owns:
//   · client identity (lead guest + contact)
//   · source / email provenance (thread + messages + communication timeline)
//   · commercial state as SEPARATE axes: stage · payment · cancellation · assignment
//   · requestedSessions[] — the per-day/per-requirement lines
// Each requested-session line materialises into canonical child `bookings` (one per required
// instructor), linked back by { bookingRequestId, requestLineId }. We never mint fake
// "Instructor 1/2" member identities — those stay as sourceInstructorLabel provenance.
//
// This module is PURE (no Firestore). Writers apply the payloads it returns.

import { normalizeBookingRequest } from './bookingDomain';
import { normalizePaymentState, PAYMENT } from './paymentSemantics';
import { isClientBooking } from './scheduleModel';
import { wallClockToInstant, opsDateKey, opsTimeLabel, LEGACY_OPS_TIMEZONE } from './opsTime';

// Pass 82C.2 §11/§13 — a booking case carries the ORGANIZATION's wall-clock timezone so its request lines
// materialize into the correct absolute instants. New (generic) cases MUST carry an explicit timezone (threaded from
// the selected org through the New-booking wizard). A legacy timezone-less case is deliberately interpreted as
// Asia/Tokyo — the SINGLE, explicit SAS-compatibility boundary, never a silent default for a new tenant's case.
export const LEGACY_CASE_TIMEZONE = LEGACY_OPS_TIMEZONE;
export function resolveCaseTimeZone(caseObj = {}, explicit = '') {
  return (caseObj && (caseObj.timezone || caseObj.timeZone)) || explicit || LEGACY_CASE_TIMEZONE;
}

const str = (v) => String(v == null ? '' : v).trim();
const arr = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
const num = (v, d = null) => (v === '' || v == null || Number.isNaN(Number(v)) ? d : Number(v));

// ── Separate status axes (item 8) ────────────────────────────────────────────
// Case STAGE (commercial lifecycle) — distinct from payment, cancellation, assignment.
export const CASE_STAGE = { INQUIRY: 'inquiry', CONFIRMED: 'confirmed', COMPLETED: 'completed', CANCELLED: 'cancelled' };
export const CASE_STAGE_LABEL = { inquiry: 'Inquiry', confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled' };

// Map Sam's single workbook "Status" (Reservation/Inquiry · Deposit Paid · Confirmed not Paid ·
// Fully Paid) → a STAGE. Payment is derived separately via normalizePaymentState. See
// docs/PASS_50A_WORKBOOK_CROSSWALK.md. We do NOT invent a "Quoted" stage (Sam has none).
export function deriveCaseStage(rawStatus, fallback = CASE_STAGE.INQUIRY) {
  const t = str(rawStatus).toLowerCase();
  if (!t) return fallback;
  if (/cancel/.test(t)) return CASE_STAGE.CANCELLED;
  if (/complete|finished|done/.test(t)) return CASE_STAGE.COMPLETED;
  if (/reservation|inquiry|enquiry|request|pending|quote/.test(t)) return CASE_STAGE.INQUIRY;
  if (/deposit|paid|confirm/.test(t)) return CASE_STAGE.CONFIRMED;
  return fallback;
}

// ── Requested-session line ────────────────────────────────────────────────────
// One operational requirement inside a case, e.g. "29 Dec · Ski · Full day · 2 instructors".
export function normalizeRequestLine(line = {}, idx = 0) {
  return {
    lineId: str(line.lineId) || `line-${idx + 1}`,
    date: str(line.date),                               // JST 'YYYY-MM-DD'
    startTime: str(line.startTime),                     // JST 'HH:MM' or ''
    endTime: str(line.endTime),
    durationCategory: str(line.durationCategory),       // full_day | half_day | ...
    activityType: str(line.activityType) || 'lesson',
    bookingType: str(line.bookingType),                 // e.g. 'MOS - Full Day'
    discipline: str(line.discipline),                   // ski | snowboard | mix_*
    skillLevel: str(line.skillLevel),
    instructorsRequired: Math.max(0, num(line.instructorsRequired, 1)),
    location: str(line.location),
    pickup: str(line.pickup) || str(line.location),
    // Pass 82E — structured operating-area / service-location / meeting-point ids on the request line (carried through
    // materialization to the child booking; empty = unresolved). `location`/`pickup` stay as the raw meeting text.
    operatingAreaId: str(line.operatingAreaId),
    serviceLocationId: str(line.serviceLocationId),
    meetingPointId: str(line.meetingPointId),
    groupSize: num(line.groupSize, null),
    notes: str(line.notes),
    sourceExternalSessionId: str(line.sourceExternalSessionId), // Accelerator B — durable external session lineage
    // §G line LIFECYCLE only — NOT assignment (assignment is derived from child bookings, k/n).
    status: str(line.status) || 'active',               // active | cancelled | completed
    // §2 canonical, PERSISTED slot cancellations (1-based indices). Survives retry/edit/stage
    // change / re-materialisation until an explicit restore. Never inferred from the child.
    cancelledSlots: arr(line.cancelledSlots).map((n) => Number(n)).filter((n) => Number.isInteger(n) && n >= 1),
  };
}

// §2 the effective ACTIVE instructor requirement for a line = required minus in-range cancelled
// slots. THE single source of truth for required-count across every consumer.
export function effectiveRequired(line = {}) {
  const req = Math.max(0, num(line.instructorsRequired, 1));
  const cancelledInRange = new Set((arr(line.cancelledSlots) || []).map(Number).filter((n) => n >= 1 && n <= req));
  return Math.max(0, req - cancelledInRange.size);
}
const isSlotCancelled = (line, slot) => arr(line.cancelledSlots).map(Number).includes(slot);

// §F duration category from the SAS Booking Type ("… Full Day" / "… Half Day").
export function durationFromBookingType(bookingType) {
  const t = str(bookingType).toLowerCase();
  if (/half.?day/.test(t)) return 'half_day';
  if (/full.?day/.test(t)) return 'full_day';
  return '';
}
const DURATION_HOURS = { full_day: 6, half_day: 3 };

// §1 THE ONE shared derivation of operational booking fields from a request line (incl. startAt/
// endAt). Used by BOTH initial materialisation and existing-child synchronisation so a line edit
// moves the Calendar/Day booking with it — no two truths. Pass 82C.2 §12 — the wall-clock (date + startTime/endTime)
// becomes an instant in the CASE's organization timezone (explicit `timeZone` option; legacy SAS/JST fallback), never
// the browser timezone. Duration-derived end remains elapsed-time addition from the resolved instant.
export function operationalFieldsFromRequestLine(line = {}, { timeZone = LEGACY_CASE_TIMEZONE } = {}) {
  const ln = normalizeRequestLine(line);
  const durationCategory = ln.durationCategory || durationFromBookingType(ln.bookingType);
  const startAt = ln.date && ln.startTime ? wallClockToInstant(ln.date, ln.startTime, timeZone) : null;
  let endAt = ln.date && ln.endTime ? wallClockToInstant(ln.date, ln.endTime, timeZone) : null; // explicit endTime wins
  if (!endAt && startAt && DURATION_HOURS[durationCategory]) {
    endAt = new Date(startAt.getTime() + DURATION_HOURS[durationCategory] * 3600 * 1000);
  }
  return {
    serviceDate: ln.date || '',
    startAt,
    endAt,
    durationCategory,
    activityType: ln.activityType || 'lesson',
    serviceType: ln.bookingType,
    discipline: ln.discipline,
    skillLevel: ln.skillLevel,
    locationLabel: ln.pickup || ln.location,
    // Pass 82E — carry the structured location ids onto the materialized child booking (explicit-only; never inferred).
    operatingAreaId: ln.operatingAreaId,
    serviceLocationId: ln.serviceLocationId,
    meetingPointId: ln.meetingPointId,
    groupSize: ln.groupSize,
    notes: ln.notes,
  };
}

// §11 canonical case COMMERCIAL state (stage + payment) from Sam's mixed workbook Status or from
// explicit canonical inputs. Reservation/Inquiry → { inquiry, not_recorded } — NEVER PAYMENT.INQUIRY.
export function deriveCanonicalCaseCommercialState(source = {}) {
  const rawStage = str(source.stage).toLowerCase();
  const rawPay = str(source.paymentState || source.status);
  const p = rawPay.toLowerCase();
  const CANON = [PAYMENT.PAID, PAYMENT.DEPOSIT, PAYMENT.NOT_PAID, PAYMENT.NOT_RECORDED];
  let paymentState = CANON.includes(rawPay) ? rawPay : null;
  if (!paymentState) {
    if (/deposit/.test(p)) paymentState = PAYMENT.DEPOSIT;
    else if (/not.?paid|awaiting|outstanding|balance due|payment due/.test(p)) paymentState = PAYMENT.NOT_PAID; // before "paid"
    else if (/fully.?paid|paid in full|^paid$/.test(p)) paymentState = PAYMENT.PAID;
    else if (/reservation|inquiry|enquiry/.test(p)) paymentState = PAYMENT.NOT_RECORDED;
  }
  const s = `${rawStage} ${p}`;
  let stage;
  if (/cancel/.test(s)) stage = CASE_STAGE.CANCELLED;
  else if (/complete|finished/.test(s)) stage = CASE_STAGE.COMPLETED;
  else if (rawStage === CASE_STAGE.CONFIRMED || [PAYMENT.PAID, PAYMENT.DEPOSIT, PAYMENT.NOT_PAID].includes(paymentState)) stage = CASE_STAGE.CONFIRMED;
  else stage = CASE_STAGE.INQUIRY;
  return { stage, paymentState: paymentState || PAYMENT.NOT_RECORDED };
}

// §E only a CONFIRMED (or completed) case materialises operational child bookings. An inquiry is
// demand only — visible in Requests, never a silent confirmed schedule obligation.
export function caseShouldMaterializeOperationalSessions(caseObj = {}) {
  return caseObj.stage === CASE_STAGE.CONFIRMED || caseObj.stage === CASE_STAGE.COMPLETED;
}

// ── Communication / source-provenance entry (email-ready, item 20) ────────────
export function normalizeCommunication(c = {}, idx = 0) {
  return {
    id: str(c.id) || `comm-${idx + 1}`,
    channel: str(c.channel) || 'email',                 // email | note | phone | whatsapp
    direction: str(c.direction) || 'inbound',
    threadId: str(c.threadId),
    messageId: str(c.messageId),
    from: str(c.from),
    receivedAt: c.receivedAt || null,
    subject: str(c.subject),
    snippet: str(c.snippet),
    summary: str(c.summary),
    attachments: arr(c.attachments),
  };
}

// ── The booking case ──────────────────────────────────────────────────────────
export function normalizeBookingCase(record = {}, fallbackId = '') {
  const base = normalizeBookingRequest(record, fallbackId); // legacy single-session fields preserved
  // §11 canonical commercial state (stage + payment as separate canonical enums).
  const commercial = deriveCanonicalCaseCommercialState(record);
  return {
    ...base,
    isCase: true,
    // §9 explicit persisted marker distinguishing a Pass-50A case from a legacy bookingRequest.
    caseModelVersion: record.caseModelVersion || null,
    seasonId: str(record.seasonId),
    // Pass 82C.2 §11 — the organization wall-clock timezone this case's request lines are expressed in. New cases
    // carry it explicitly (wizard threads the selected org's zone); a legacy timezone-less case stays '' and is
    // interpreted as Asia/Tokyo at materialization (the explicit SAS-compat boundary in resolveCaseTimeZone).
    timezone: str(record.timezone || record.timeZone),
    // Pass 82E — case-level operating area (a new case carries an explicit area; child bookings inherit it when their
    // request line does not specify one). Structured, never inferred from free text.
    operatingAreaId: str(record.operatingAreaId),
    // CLIENT
    clientId: str(record.clientId),
    leadGuest: record.leadGuest || (base.requesterName ? { fullName: base.requesterName } : null),
    additionalGuests: arr(record.additionalGuests),
    // SOURCE / EMAIL PROVENANCE (item 20)
    sourceType: base.sourceType || 'manual',
    sourceReference: str(record.sourceReference || record.roombossNumber), // e.g. RoomBoss no.
    sourceThreadId: str(record.sourceThreadId),
    sourceMessageIds: arr(record.sourceMessageIds),
    sourceReceivedAt: record.sourceReceivedAt || null,
    // Accelerator D — durable back-reference to the originating SourceIntake (provenance, NOT identity/doc-id).
    sourceIntakeId: str(record.sourceIntakeId),
    communications: arr(record.communications).map(normalizeCommunication),
    // COMMERCIAL — separate CANONICAL axes (items 8/11). Reservation/Inquiry → not_recorded.
    stage: commercial.stage,
    paymentState: commercial.paymentState,                       // canonical PAYMENT enum
    cancellationResolution: str(record.cancellationResolution),
    invoiceNumber: str(record.invoiceNumber),
    roombossNumber: str(record.roombossNumber || record.sourceReference),
    priceSnapshot: num(record.priceSnapshot ?? record.servicePricingSnapshot, null),
    currency: str(record.currency) || 'JPY',
    referral: str(record.referral),
    // TRIP
    accommodationName: str(record.accommodationName),
    accommodationAddress: str(record.accommodationAddress),
    accommodationMapUrl: str(record.accommodationMapUrl),
    tripNotes: str(record.tripNotes || record.notes),
    // REQUESTED WORK (item 3)
    requestedSessions: arr(record.requestedSessions).map(normalizeRequestLine),
  };
}

// Read helpers keeping the axes separate.
export const casePaymentState = (c) => normalizePaymentState(c.paymentState);
export const caseIsCancelled = (c) => c.stage === CASE_STAGE.CANCELLED;

// §12 the codebase has two payment vocabularies: the case/ops canonical `PAYMENT`
// (paymentSemantics) and the booking's `PAYMENT_STATES` (scheduleModel) that normalizeBooking
// preserves. A child booking must store the scheduleModel form so it survives read-normalisation
// AND still maps back to the same PAYMENT enum for the chip/legend — so parent + child DISPLAY
// the same payment. (Inquiry cases materialise no children.)
const CASE_PAY_TO_BOOKING = {
  [PAYMENT.DEPOSIT]: 'deposit_paid',
  [PAYMENT.NOT_PAID]: 'confirmed_not_paid',
  [PAYMENT.PAID]: 'fully_paid',
  [PAYMENT.INQUIRY]: 'reservation_inquiry',
  [PAYMENT.NOT_RECORDED]: '',
};
export function bookingPaymentFromCasePayment(casePay) {
  const canon = normalizePaymentState(casePay);
  return CASE_PAY_TO_BOOKING[canon] ?? '';
}

// ── Assignment progress across a case (item 4/6) ──────────────────────────────
// Given the case + its child bookings, compute k/n assigned per line and overall.
export function caseAssignmentProgress(caseObj, bookings = []) {
  // §P synthesized read-only cases resolve assignment from their source bookings directly
  // (the imported bookings carry no requestLineId link).
  if (caseObj.synthesized) {
    const assignedByLine = new Map((caseObj._synthLines || []).map((l) => [l.lineId, !!l.assignedInstructorId]));
    let required = 0, assigned = 0;
    const lines = (caseObj.requestedSessions || []).map((ln) => {
      const a = assignedByLine.get(ln.lineId) ? 1 : 0;
      required += ln.instructorsRequired; assigned += a;
      return { ...ln, required: ln.instructorsRequired, assigned: a };
    });
    return { required, assigned, complete: required > 0 && assigned >= required, remaining: Math.max(0, required - assigned), lines };
  }
  const byLine = {};
  for (const b of bookings) {
    if (b.bookingRequestId !== caseObj.id) continue;
    if (!b.requestLineId || !b.instructorId || b.status === 'canceled') continue;
    (byLine[b.requestLineId] = byLine[b.requestLineId] || new Set()).add(b.instructorId);
  }
  let required = 0, assigned = 0;
  const lines = (caseObj.requestedSessions || []).map((ln) => {
    if (ln.status === 'cancelled') return { ...ln, required: 0, assigned: 0 };
    // §2 required is the EFFECTIVE requirement (minus explicitly cancelled slots).
    const req = effectiveRequired(ln);
    const a = byLine[ln.lineId] ? byLine[ln.lineId].size : 0;
    required += req;
    assigned += Math.min(a, req);
    return { ...ln, required: req, assigned: Math.min(a, req) };
  });
  return { required, assigned, complete: required > 0 && assigned >= required, remaining: Math.max(0, required - assigned), lines };
}

// Instructor-DAYS summary for the Requests table (item 6) — Σ EFFECTIVE required over active lines.
export function caseInstructorDays(caseObj) {
  return (caseObj.requestedSessions || []).filter((l) => l.status !== 'cancelled').reduce((n, l) => n + effectiveRequired(l), 0);
}

// ── Pass 65 — the ONE operator flow derivation (case → booking → schedule) ──────
// Pure. The operator journey is NOT a persisted status: it is DERIVED from three canonical axes —
// case STAGE, MATERIALISATION (expected vs created child bookings), and ASSIGNMENT (assigned vs
// unassigned children). Every UI reads these numbers from here (never recomputes them), and reuses
// effectiveRequired()/caseAssignmentProgress() rather than duplicating Pass-62 logic. A child booking
// is one materialised slot; "Scheduled" is a DERIVED label, never a case-stage enum value.
export function caseChildBookings(caseObj, bookings = []) {
  return (bookings || []).filter((b) => b.bookingRequestId === caseObj.id && b.status !== 'canceled' && b.status !== 'cancelled');
}

// Enumerate the ACTIVE slots of a case (skipping cancelled slots/lines) with their child booking + state.
export function caseSlotRows(caseObj, bookings = []) {
  if (!caseObj || caseObj.synthesized) return [];
  const children = caseChildBookings(caseObj, bookings);
  const bySlot = new Map(children.map((b) => [b.requestSlotId, b]));
  const rows = [];
  for (const ln of (caseObj.requestedSessions || [])) {
    if (ln.status === 'cancelled') continue;
    const req = Math.max(0, num(ln.instructorsRequired, 1));
    for (let s = 1; s <= req; s += 1) {
      if (isSlotCancelled(ln, s)) continue;               // §24 cancelled slots are not operational demand
      const requestSlotId = `${ln.lineId}#${s}`;
      const booking = bySlot.get(requestSlotId) || null;
      const state = !booking ? 'not_created' : (booking.instructorId ? 'assigned' : 'unassigned');
      rows.push({
        lineId: ln.lineId, slot: s, requestSlotId,
        date: str(ln.date), startTime: str(ln.startTime), bookingType: str(ln.bookingType),
        discipline: str(ln.discipline), skillLevel: str(ln.skillLevel), location: str(ln.pickup) || str(ln.location),
        multi: req > 1,
        booking, bookingId: booking ? booking.id : '',
        state,
        instructorId: booking ? (booking.instructorId || '') : '',
        instructorName: booking ? (booking.instructorNameSnapshot || '') : '',
      });
    }
  }
  return rows;
}

export function deriveCaseOperationalFlow(caseObj = {}, bookings = []) {
  const prog = caseAssignmentProgress(caseObj, bookings);   // required = EFFECTIVE (respects cancelledSlots)
  const children = caseChildBookings(caseObj, bookings);
  const stage = caseObj.stage;
  const synthesized = !!caseObj.synthesized;
  const expectedSlots = prog.required;
  const createdSlots = children.length;
  const missingSlots = Math.max(0, expectedSlots - createdSlots);
  const assignedSlots = children.filter((b) => b.instructorId).length;
  const unassignedSlots = children.filter((b) => !b.instructorId).length;

  const isFullyMaterialized = expectedSlots > 0 && createdSlots >= expectedSlots;
  const isFullyAssigned = expectedSlots > 0 && assignedSlots >= expectedSlots && missingSlots === 0;
  const canConfirm = !synthesized && stage === CASE_STAGE.INQUIRY && expectedSlots > 0;
  const canMaterializeMissing = !synthesized && stage === CASE_STAGE.CONFIRMED && missingSlots > 0;
  const canAssign = !synthesized && (stage === CASE_STAGE.CONFIRMED || stage === CASE_STAGE.COMPLETED) && unassignedSlots > 0;
  const isScheduled = !synthesized && (stage === CASE_STAGE.CONFIRMED || stage === CASE_STAGE.COMPLETED) && isFullyMaterialized && isFullyAssigned;

  let nextAction = { kind: 'none', label: '' };
  if (synthesized) nextAction = { kind: 'none', label: 'Source-derived — read-only' };
  else if (stage === CASE_STAGE.CANCELLED) nextAction = { kind: 'none', label: 'Cancelled' };
  else if (canConfirm) nextAction = { kind: 'confirm', label: `Confirm & create ${expectedSlots} booking${expectedSlots === 1 ? '' : 's'}` };
  else if (canMaterializeMissing) nextAction = { kind: 'materialize', label: `Create ${missingSlots} missing booking${missingSlots === 1 ? '' : 's'}` };
  else if (canAssign) nextAction = { kind: 'assign', label: `Assign ${unassignedSlots} instructor${unassignedSlots === 1 ? '' : 's'}` };
  else if (isScheduled) nextAction = { kind: 'scheduled', label: 'Scheduled' };

  return {
    stage, synthesized,
    expectedSlots, createdSlots, missingSlots, assignedSlots, unassignedSlots,
    isFullyMaterialized, isFullyAssigned, isScheduled,
    canConfirm, canMaterializeMissing, canAssign,
    nextAction,
  };
}

// ── Read-only synthesized-case bridge for imported source bookings (item P) ───
// The existing 26/27 source bookings have no parent case. Group ORPHAN client bookings (no
// bookingRequestId) into read-only synthesized cases so Requests isn't misleadingly empty — with
// NO writes, NO regenerated bookings, NO persisted fake ids. Grouping uses safe source identifiers
// (RoomBoss/Invoice if present, else client+first-date); ambiguous rows stay separate, never merged.
// Instructor 1/2 stays a source label only.
// A booking carries trustworthy SOURCE provenance (only these are bridged; never guess).
function hasSourceProvenance(b) {
  return !!(str(b.sourceInstructorLabel) || b.importedFromWorkbook || b.bootstrapImport || str(b.sourceReference) || str(b.sourceRecordId));
}
// §15 grouping uses ONLY strong shared identifiers. If none, ONE synthesized case per source
// booking — we never merge by name+day (that could merge two genuinely separate bookings).
function synthKey(b) {
  const rb = str(b.roombossNumber || b.sourceReference);
  if (rb) return `rb:${rb}`;
  const inv = str(b.invoiceNumber);
  if (inv) return `inv:${inv}`;
  return `src:${b.id}`; // one case per source booking — no name+day grouping
}
export function synthesizeCasesFromBookings(bookings = []) {
  // §15 only orphan client bookings WITH source provenance are bridged (e.g. the 26/27 workbook
  // import). 24/25 & 25/26 name-only history is NOT reconstructed into cases.
  const orphans = bookings.filter((b) => !b.bookingRequestId && isClientBooking(b) && b.status !== 'canceled' && hasSourceProvenance(b));
  const groups = new Map();
  for (const b of orphans) { const k = synthKey(b); if (!groups.has(k)) groups.set(k, []); groups.get(k).push(b); }
  return [...groups.entries()].map(([k, list]) => {
    const first = list[0];
    const base = normalizeBookingCase({
      id: `synth:${k}`, organizationId: first.organizationId, seasonId: first.seasonId,
      requesterName: first.requesterName, requesterEmail: first.requesterEmail, requesterPhone: first.requesterPhone,
      sourceType: 'source_import', // §15 the real source (workbook/import), not "manual"
      status: first.paymentState,  // stage derived from workbook status
      requestedSessions: list.map((b, i) => ({
        lineId: `${k}#${i + 1}`, date: b.serviceDate || opsDateKey(b.startAt),
        startTime: b.startAt ? opsTimeLabel(b.startAt) : '', // §15 preserve a trustworthy start time
        activityType: 'lesson', bookingType: b.serviceType, discipline: b.discipline, skillLevel: b.skillLevel,
        instructorsRequired: 1, location: b.locationLabel, status: 'active',
      })),
    });
    return {
      ...base,
      synthesized: true,
      readOnly: true,
      sourceLabels: [...new Set(list.map((b) => b.sourceInstructorLabel).filter(Boolean))],
      _synthLines: list.map((b, i) => ({ lineId: `${k}#${i + 1}`, assignedInstructorId: b.instructorId || '' })),
    };
  });
}

// ── Scannable Requests-table row (item 6) ─────────────────────────────────────
export function buildCaseRow(caseObj, bookings = []) {
  const prog = caseAssignmentProgress(caseObj, bookings);
  const dates = (caseObj.requestedSessions || []).map((l) => l.date).filter(Boolean).sort();
  const uniqueDays = [...new Set(dates)];
  return {
    id: caseObj.id,
    name: caseObj.requesterName || caseObj.leadGuest?.fullName || 'New enquiry',
    dateStart: dates[0] || '',
    dateEnd: dates[dates.length - 1] || '',
    days: uniqueDays.length,
    instructorDays: caseInstructorDays(caseObj),
    assigned: prog.assigned,
    required: prog.required,
    assignmentLabel: `${prog.assigned} / ${prog.required} assigned`,
    stage: caseObj.stage,
    paymentState: casePaymentState(caseObj),
    invoiceNumber: caseObj.invoiceNumber,
    source: caseObj.sourceType,
    updatedAt: caseObj.updatedAt || null,
    // needs attention: confirmed but under-assigned, or inquiry awaiting action
    attention: (caseObj.stage === CASE_STAGE.CONFIRMED && !prog.complete) || caseObj.stage === CASE_STAGE.INQUIRY,
  };
}

// ── Materialise child operational bookings from a line (items 4/F/H) ──────────
// PURE and SLOT-SAFE: every required instructor is a stable slot `${lineId}#${n}`. Returns the
// canonical payloads for the slots still MISSING (existing linked slots are matched by
// requestSlotId; legacy links without a slotId reduce the gap so retries never duplicate).
// Payloads are UNASSIGNED demand (surface in Unassigned) — no fake instructor identities. Only
// materialises when the case stage says so (§E); an inquiry line yields nothing.
export function materializeLineBookings(caseObj, line, { existingBookings = [] } = {}) {
  const ln = normalizeRequestLine(line);
  if (ln.status === 'cancelled' || ln.status === 'completed') return [];
  if (!caseShouldMaterializeOperationalSessions(caseObj)) return []; // §E inquiry = demand only

  const existing = existingBookings.filter(
    (b) => b.bookingRequestId === caseObj.id && b.requestLineId === ln.lineId && b.status !== 'canceled',
  );
  const taken = new Set(existing.map((b) => b.requestSlotId).filter(Boolean));

  // Pass 82C.2 §12/§13 — resolve the case timezone ONCE (case zone → legacy SAS/JST). Every child of this case is
  // materialized in the SAME zone and DURABLY stamped with it, so downstream slot/season derivation is org-correct.
  const caseTz = resolveCaseTimeZone(caseObj);
  // §1 the shared operational-field derivation (identical to line-edit sync), in the case's timezone.
  const opFields = operationalFieldsFromRequestLine(ln, { timeZone: caseTz });

  const out = [];
  for (let s = 1; s <= ln.instructorsRequired; s += 1) {
    const requestSlotId = `${ln.lineId}#${s}`;
    if (taken.has(requestSlotId)) continue;
    if (isSlotCancelled(ln, s)) continue; // §2 explicitly cancelled slots are never re-materialised
    out.push({
      organizationId: caseObj.organizationId,
      seasonId: caseObj.seasonId,
      bookingRequestId: caseObj.id,
      requestLineId: ln.lineId,
      requestSlotIndex: s,
      requestSlotId,           // §H stable slot identity (NOT an instructor identity; may stay unassigned)
      sourceExternalSessionId: ln.sourceExternalSessionId || '', // Accelerator B — external session lineage → booking
      instructorId: '',
      assignmentState: 'unassigned',
      status: 'confirmed',     // §F explicit lifecycle (case is confirmed-stage to reach here)
      scheduleEntryType: 'client_booking',
      timezone: caseTz,        // Pass 82C.2 §7 — child carries the org wall-clock zone (drives deriveSlots/season)
      ...opFields,             // §1/§F serviceDate, startAt, endAt, durationCategory, discipline, etc.
      // Pass 82E — child inherits the CASE's operating area when the request line did not specify one (explicit only).
      operatingAreaId: opFields.operatingAreaId || caseObj.operatingAreaId || '',
      // Pass 63 §21 — every canonical child booking DURABLY inherits the case's stable client identity
      // (clientId + the linking evidence). Retry/re-materialisation cannot change it: the identity is a
      // property of the case, not minted per booking. No per-booking client ids.
      clientId: caseObj.clientId || '',
      requesterUserId: caseObj.requesterUserId || '',
      requesterName: caseObj.requesterName || caseObj.leadGuest?.fullName || '',
      requesterEmail: caseObj.requesterEmail || caseObj.leadGuest?.email || '',
      requesterPhone: caseObj.requesterPhone || caseObj.leadGuest?.phone || '',
      // §12 child payment (booking vocab) mirrors the parent's canonical payment — survives
      // normalizeBooking read AND maps back to the same PAYMENT enum for display.
      paymentState: bookingPaymentFromCasePayment(caseObj.paymentState),
      sourceInstructorLabel: '', // real cases carry no source label; provenance-only labels stay on imported rows
    });
  }
  // Legacy links without a slotId can't be matched by id — reduce the gap by their count so
  // repeated materialisation under retry/concurrency cannot overfill.
  const legacyNoSlot = existing.length - taken.size;
  return legacyNoSlot > 0 ? out.slice(0, Math.max(0, out.length - legacyNoSlot)) : out;
}

// All child bookings for a whole case (all lines).
export function materializeCaseBookings(caseObj, { existingBookings = [] } = {}) {
  return (caseObj.requestedSessions || []).flatMap((ln) => materializeLineBookings(caseObj, ln, { existingBookings }));
}

// ── Email → proposed case update (item 21) ────────────────────────────────────
// Fields we NEVER silently overwrite from an ambiguous email — they always go to review.
export const PROTECTED_CASE_FIELDS = [
  'requestedSessions', 'stage', 'paymentState', 'cancellationResolution',
  'instructorId', 'accommodationAddress', 'pickup', 'location', 'requestedStartAt', 'requestedEndAt',
];
// Given the current case + a proposed field map extracted from an email, split into:
//   autoApply — safe NEW fields (currently empty on the case, not protected)
//   review    — protected fields, or changes to already-set values (surface "Review email update")
// Nothing here mutates the case; a human/rule decides. Provenance stays on the case.communications.
export function proposeCaseUpdateFromEmail(caseObj, proposal = {}) {
  const autoApply = {};
  const review = [];
  for (const [field, proposed] of Object.entries(proposal)) {
    const current = caseObj[field];
    const isEmpty = current == null || current === '' || (Array.isArray(current) && current.length === 0);
    if (PROTECTED_CASE_FIELDS.includes(field)) {
      review.push({ field, current: current ?? '', proposed, reason: 'protected operational field — needs explicit acceptance' });
    } else if (isEmpty && proposed !== '' && proposed != null) {
      autoApply[field] = proposed;
    } else if (str(current) !== str(proposed)) {
      review.push({ field, current: current ?? '', proposed, reason: 'changes an existing value' });
    }
  }
  return { autoApply, review };
}

export { PAYMENT };
